import { Box, CssBaseline, Grid2 } from '@mui/material';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import QlerLogo from '@assets/images/qler_logo_2.svg';
import './layout.css';
import React, { useEffect, useState } from 'react';
import CustomMenu from '@ui-components/Menu/CustomMenu';
import CustomMenuItem from '@ui-components/Menu/CustomMenuItem';
import CustomMenuList from '@ui-components/Menu/CustomMenuList';
import CustomListItemIcon from '@ui-components/List/CustomListItemIcon';
import CurrentDate from '../components/Date';
import CurrentTime from '../components/Time';

import ArrowUp from '@assets/images/arrows-up.svg';
import CheckIcon from '@assets/images/check.svg';
import CustomAvatar from '@ui-components/Avatar/CustomAvatar';
import {
  FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  CONSULT_WAITING_SCREEN_ROUTE_PATH,
  CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
} from '../../routers/RoutPath';
import { getUserFromStorage } from '../components/storageUtility';
import { ROLES } from '../../roles';
import Logout from '@assets/images/logout.svg';
import { useFontSize } from '@qler-view/shared_lib/src/shared/lib/providers/FontSizeContext';
import User from '@assets/images/user.svg';
import Email from '@assets/images/email.svg';
import CustomDialog from '@ui-components/Dialog/CustomDialog';
import ChangePassword from '../login/ChangePassword';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import ChangePwdIcon from '@assets/images/changePwdMenuIcon.svg';

export default function FacilityLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isWaitingScreen = location.pathname.startsWith(
    CONSULT_WAITING_SCREEN_ROUTE_PATH
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorUserEl, setAnchorUserEl] = useState<null | HTMLElement>(null);
  const user = getUserFromStorage();
  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  const { fontSize, setFontSize } = useFontSize();
  const [openChangePwd, setOpenChangePwd] = useState(false);
  const handleChangePwdOpen = () => setOpenChangePwd(true);
  const handleChangePwdClose = () => setOpenChangePwd(false);

  const successMessage = (message: string) => {
    setOpenToast(true);
    setMessage(message);
    setTimeout(() => {
      setOpenToast(false);
    }, 5000);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorUserEl(event.currentTarget);
  };

  useEffect(() => {
    return () => {
      document.documentElement.style.zoom = '1';
    };
  }, []);

  const handleUserMenuClose = () => {
    setAnchorUserEl(null);
  };

  const handleFacilityUserLogout = () => {
    localStorage.clear();
    localStorage.removeItem('token');
    navigate('/', { replace: true });
  };

  const handleLogoClick = () => {
    if (user?.role === ROLES.FACILITY_USER) {
      navigate(FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH);
    } else if (user?.role === ROLES.CASE_MANAGER) {
      localStorage.setItem('lastSelectedTabIndex', '0');
      const currentPath = globalThis.location.pathname;

      if (currentPath === CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH) {
        globalThis.location.reload();
      } else {
        globalThis.location.href =
          CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH;
      }
    }
  };

  const getHeaderText = () => {
    if (user?.role === ROLES.FACILITY_USER) {
      return user?.facilityName;
    } else if (user?.role === ROLES.CASE_MANAGER) {
      return 'Consult Request';
    } else {
      return '';
    }
  };

  const getUserInitials = (
    user: { firstName?: string; lastName?: string } | null | undefined
  ): string => {
    const firstInitial = user?.firstName?.charAt(0)?.toUpperCase() ?? '';
    const lastInitial = user?.lastName?.charAt(0)?.toUpperCase() ?? '';
    return `${firstInitial}${lastInitial}`;
  };
  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {openToast && (
        <CustomSnackbar
          id="facility-snackbar-success"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      <CssBaseline />
      {/* Header */}
      <Box component="header" className="header-element">
        <Grid2 container alignItems="center" className="width-100">
          <Grid2 size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
            <Box className="align-layout-center">
              <Box className="pr-24">
                <CustomIconButton
                  id="logo"
                  icon={<QlerLogo />}
                  onClick={() => {
                    if (!isWaitingScreen) handleLogoClick();
                  }}
                  className={isWaitingScreen ? 'cursor-default' : ''}
                />
              </Box>
              <CustomDivider orientation="vertical" variant="middle" flexItem />
              <CustomTypography
                id="facility-name"
                variant="h5"
                color="textPrimary"
                className="pl-24"
              >
                {getHeaderText()}
              </CustomTypography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
            <Box className="header-right-element align-layout-center">
              <Box
                className="box-border courser-pointer flex mr-4"
                onClick={handleMenuOpen}
              >
                <CustomTypography
                  className="pr-6"
                  id="date-time"
                  color="textDisabled"
                  variant="body2"
                >
                  {fontSize.charAt(0).toUpperCase() + fontSize.slice(1)}
                </CustomTypography>
                <ArrowUp />
              </Box>
              <Box className="box-border bb-width flex mr-4">
                <Box className="pr-8">
                  <CustomTypography
                    id="date-time"
                    color="textPrimary"
                    variant="body2"
                  >
                    <CurrentDate />
                  </CustomTypography>
                </Box>
                <Box>
                  <CustomTypography
                    id="date-time"
                    color="textPrimary"
                    variant="body2"
                  >
                    <CurrentTime />
                  </CustomTypography>
                </Box>
              </Box>
              <Box>
                <CustomAvatar
                  id="user-avatar"
                  alt={user?.username}
                  width={42}
                  height={42}
                  onClick={handleUserMenuOpen}
                >
                  {getUserInitials(user)}
                </CustomAvatar>
                <CustomMenu
                  id="user-menu"
                  anchorEl={anchorUserEl ?? document.body}
                  open={Boolean(anchorUserEl)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  className="mt-4"
                >
                  <CustomMenuList>
                    <CustomMenuItem className="cursor-default">
                      <CustomListItemIcon>{<User />}</CustomListItemIcon>
                      {`${user?.firstName} ${user?.lastName}`}
                    </CustomMenuItem>
                    <CustomDivider />
                    <CustomMenuItem className="cursor-default">
                      <CustomListItemIcon>{<Email />}</CustomListItemIcon>
                      {user?.email ?? 'N/A'}
                    </CustomMenuItem>
                    <CustomDivider />
                    {user?.role === ROLES.CASE_MANAGER && (
                      <>
                        <CustomMenuItem
                          onClick={() => {
                            handleUserMenuClose();
                            handleChangePwdOpen();
                          }}
                        >
                          <CustomListItemIcon>
                            {<ChangePwdIcon />}
                          </CustomListItemIcon>
                          Change Password
                        </CustomMenuItem>
                        <CustomDivider />
                      </>
                    )}
                    <CustomMenuItem
                      onClick={() => {
                        handleFacilityUserLogout();
                        handleUserMenuClose();
                      }}
                    >
                      <CustomListItemIcon>{<Logout />}</CustomListItemIcon>
                      Logout
                    </CustomMenuItem>
                  </CustomMenuList>
                </CustomMenu>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      {/* Main Content with Outlet */}
      <Box component="main" className="content-element">
        <Outlet />
      </Box>
      {/* Footer */}
      <Box component="footer" className="footer-element">
        <CustomTypography id="footer-text" variant="body3" color="textDisabled">
          Having Difficulty? Please call&nbsp;
        </CustomTypography>
        <CustomTypography
          id="footer-text"
          variant="subtitle2"
          color="textPrimary"
        >
          (855) 797-7879
        </CustomTypography>
      </Box>

      <Box>
        <CustomMenu
          id="accessibility-menu"
          anchorEl={anchorEl ?? document.body}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <CustomMenuList>
            <CustomMenuItem
              onClick={() => {
                setFontSize('small');
                handleMenuClose();
              }}
            >
              <CustomTypography id="small-font" variant="h6" className="pr-24">
                Small
              </CustomTypography>
              <CustomListItemIcon>
                {fontSize === 'small' && <CheckIcon />}
              </CustomListItemIcon>
            </CustomMenuItem>
            <CustomDivider />
            <CustomMenuItem
              onClick={() => {
                setFontSize('medium');
                handleMenuClose();
              }}
            >
              <CustomTypography id="medium-font" variant="h5" className="pr-24">
                Medium
              </CustomTypography>
              <CustomListItemIcon>
                {fontSize === 'medium' && <CheckIcon />}
              </CustomListItemIcon>
            </CustomMenuItem>
            <CustomDivider />
            <CustomMenuItem
              onClick={() => {
                setFontSize('large');
                handleMenuClose();
              }}
            >
              <CustomTypography id="large-font" variant="h4" className="pr-24">
                Large
              </CustomTypography>
              <CustomListItemIcon>
                {fontSize === 'large' && <CheckIcon />}
              </CustomListItemIcon>
            </CustomMenuItem>
          </CustomMenuList>
        </CustomMenu>
      </Box>
      <CustomDialog
        id="facility-change-password-dialog"
        open={openChangePwd}
        onClose={handleChangePwdClose}
        className="m-24"
      >
        <ChangePassword
          closePopup={handleChangePwdClose}
          successMessage={successMessage}
        />
      </CustomDialog>
    </Box>
  );
}
